import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import DashboardLayout from '../../components/DashboardLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Calendar, Loader2, AlertTriangle, MapPin, CheckCircle2, Clock, ChevronDown, ChevronUp, Wallet, Lock, History } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Activities = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [activities, setActivities] = useState([]);
    const [loading, setLoading] = useState(true);
    const [polling, setPolling] = useState(false);
    const [confirmDialog, setConfirmDialog] = useState(null); // { activity, students:[], phase, amount, customAmount? }
    const [partialDialog, setPartialDialog] = useState(null); // { activity, student, remaining }
    const [partialAmountInput, setPartialAmountInput] = useState('');
    // Map: activityId -> Set of selected student_ids (for batch pay)
    const [selectedByActivity, setSelectedByActivity] = useState({});
    // Expanded participated activities (accordion state)
    const [expandedParticipated, setExpandedParticipated] = useState(() => new Set());

    // Stripe return: poll session status, then refresh
    useEffect(() => {
        const qp = new URLSearchParams(location.search);
        const sessionId = qp.get('session_id');
        if (!sessionId) { fetchData(); return; }
        setPolling(true);
        let attempts = 0;
        const poll = async () => {
            attempts++;
            try {
                const r = await axios.get(`${API_URL}/api/payments/checkout/status/${sessionId}`, { withCredentials: true });
                if (r.data.payment_status === 'paid') {
                    toast.success('Payment successful!');
                    navigate(location.pathname, { replace: true });
                    await fetchData();
                    setPolling(false); return;
                }
            } catch (err) { console.debug('checkout/status poll error:', err?.message); }
            if (attempts < 15) setTimeout(poll, 3000);
            else { navigate(location.pathname, { replace: true }); await fetchData(); setPolling(false); }
        };
        poll();
        // eslint-disable-next-line
    }, []);

    const fetchData = async () => {
        setLoading(true);
        try {
            const r = await axios.get(`${API_URL}/api/activities/parent/eligible`, { withCredentials: true });
            setActivities(r.data || []);
        } catch { toast.error('Failed to load activities'); }
        finally { setLoading(false); }
    };

    // Split into "New Activities" vs "Participated"
    const { newActivities, participated } = useMemo(() => {
        const nw = [];
        const pt = [];
        for (const a of activities) {
            if (a.has_registration) pt.push(a);
            else nw.push(a);
        }
        return { newActivities: nw, participated: pt };
    }, [activities]);

    const startCheckout = async ({ activityId, studentIds, phase, customAmount }) => {
        try {
            const body = { activity_id: activityId, phase };
            if (Array.isArray(studentIds)) body.student_ids = studentIds;
            else body.student_id = studentIds;
            if (phase === 'partial' && customAmount != null) body.custom_amount = Number(customAmount);
            const r = await axios.post(`${API_URL}/api/payments/checkout/activity`, body, { withCredentials: true });
            if (r.data?.url) window.location.href = r.data.url;
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Checkout failed');
        }
    };

    const openConfirmSingle = (activity, student, phase) => {
        const cost = Number(activity.cost || 0);
        const deposit = Number(activity.deposit || 0);
        const paid = Number(student.amount_paid || 0);
        const discount = Number(student.discount_applied || 0);
        let perAmt;
        if (phase === 'deposit') perAmt = Math.min(deposit, cost);
        else if (phase === 'balance') perAmt = Math.max(0, cost - paid - discount);
        else perAmt = Math.max(0, cost - paid - discount);
        setConfirmDialog({ activity, students: [student], phase, amount: perAmt });
    };

    const openConfirmBatch = (activity, selectedStudents, phase) => {
        const cost = Number(activity.cost || 0);
        const deposit = Number(activity.deposit || 0);
        let amount = 0;
        for (const s of selectedStudents) {
            const paid = Number(s.amount_paid || 0);
            const discount = Number(s.discount_applied || 0);
            if (phase === 'deposit') amount += Math.min(deposit, cost);
            else amount += Math.max(0, cost - paid - discount);
        }
        setConfirmDialog({ activity, students: selectedStudents, phase, amount });
    };

    const openPartial = (activity, student) => {
        const cost = Number(activity.cost || 0);
        const paid = Number(student.amount_paid || 0);
        const discount = Number(student.discount_applied || 0);
        const remaining = Math.max(0, cost - paid - discount);
        setPartialAmountInput('');
        setPartialDialog({ activity, student, remaining });
    };

    const submitPartial = () => {
        if (!partialDialog) return;
        const amt = Number(partialAmountInput);
        if (!amt || amt <= 0) { toast.error('Enter a positive amount'); return; }
        if (amt > partialDialog.remaining + 0.01) { toast.error(`Cannot exceed remaining €${partialDialog.remaining.toFixed(2)}`); return; }
        setConfirmDialog({
            activity: partialDialog.activity,
            students: [partialDialog.student],
            phase: 'partial',
            amount: amt,
            customAmount: amt,
        });
        setPartialDialog(null);
    };

    const toggleSelection = (activityId, studentId) => {
        setSelectedByActivity(prev => {
            const cur = new Set(prev[activityId] || []);
            if (cur.has(studentId)) cur.delete(studentId); else cur.add(studentId);
            return { ...prev, [activityId]: cur };
        });
    };
    const clearSelection = (activityId) => {
        setSelectedByActivity(prev => ({ ...prev, [activityId]: new Set() }));
    };

    const toggleExpanded = (activityId) => {
        setExpandedParticipated(prev => {
            const s = new Set(prev);
            if (s.has(activityId)) s.delete(activityId); else s.add(activityId);
            return s;
        });
    };

    if (loading || polling) return (
        <DashboardLayout>
            <div className="flex flex-col items-center justify-center h-64 gap-3">
                <Loader2 className="h-8 w-8 animate-spin text-[#064E3B]" />
                {polling && <p className="text-sm text-[#4B5563]">Confirming your payment…</p>}
            </div>
        </DashboardLayout>
    );

    return (
        <DashboardLayout>
            <div className="space-y-8" data-testid="parent-activities-page">
                <div>
                    <h1 className="text-2xl font-bold text-[#1F2937] font-['Outfit']">Activities</h1>
                    <p className="text-[#4B5563]">Camps, trips and events for your children</p>
                </div>

                {/* ============ NEW ACTIVITIES SECTION ============ */}
                <section data-testid="new-activities-section">
                    <div className="flex items-center gap-2 mb-3">
                        <Calendar className="h-5 w-5 text-[#064E3B]" />
                        <h2 className="text-lg font-semibold text-[#064E3B] font-['Outfit']">New Activities</h2>
                        <Badge variant="secondary" className="ml-1">{newActivities.length}</Badge>
                    </div>
                    {newActivities.length === 0 ? (
                        <Card className="card-surface">
                            <CardContent className="flex flex-col items-center justify-center py-10">
                                <Calendar className="h-10 w-10 text-[#4B5563]/30 mb-2" />
                                <p className="text-[#4B5563]">No new activities available</p>
                                <p className="text-xs text-[#6B7280] mt-1">Check back later or contact the school for upcoming events.</p>
                            </CardContent>
                        </Card>
                    ) : (
                        <div className="space-y-4">
                            {newActivities.map(a => (
                                <ActivityRow
                                    key={a.id}
                                    activity={a}
                                    onPaySingle={openConfirmSingle}
                                    onPayBatch={openConfirmBatch}
                                    selected={selectedByActivity[a.id] || new Set()}
                                    onToggle={toggleSelection}
                                    onClearSelection={clearSelection}
                                />
                            ))}
                        </div>
                    )}
                </section>

                {/* ============ PARTICIPATED ACTIVITIES SECTION ============ */}
                <section data-testid="participated-activities-section">
                    <div className="flex items-center gap-2 mb-3">
                        <History className="h-5 w-5 text-[#7C3AED]" />
                        <h2 className="text-lg font-semibold text-[#7C3AED] font-['Outfit']">Participated Activities</h2>
                        <Badge variant="secondary" className="ml-1">{participated.length}</Badge>
                    </div>
                    {participated.length === 0 ? (
                        <Card className="card-surface">
                            <CardContent className="flex flex-col items-center justify-center py-10">
                                <History className="h-10 w-10 text-[#4B5563]/30 mb-2" />
                                <p className="text-[#4B5563]">No participations yet</p>
                                <p className="text-xs text-[#6B7280] mt-1">Activities you or your children join will appear here for records.</p>
                            </CardContent>
                        </Card>
                    ) : (
                        <div className="space-y-4">
                            {participated.map(a => (
                                <ParticipatedActivityCard
                                    key={a.id}
                                    activity={a}
                                    expanded={expandedParticipated.has(a.id)}
                                    onToggle={() => toggleExpanded(a.id)}
                                    onPaySingle={openConfirmSingle}
                                    onPayPartial={openPartial}
                                />
                            ))}
                        </div>
                    )}
                </section>

                {/* Partial-amount input dialog */}
                <Dialog open={!!partialDialog} onOpenChange={(o) => !o && setPartialDialog(null)}>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle className="font-['Outfit']">Pay a partial amount</DialogTitle>
                            <DialogDescription>Enter any amount up to the remaining balance.</DialogDescription>
                        </DialogHeader>
                        {partialDialog && (
                            <div className="space-y-3 text-sm">
                                <div className="bg-[#FAF9F6] rounded-lg p-3 space-y-1">
                                    <p><span className="text-[#6B7280]">Child:</span> <span className="font-medium">{partialDialog.student.student_name}</span></p>
                                    <p><span className="text-[#6B7280]">Activity:</span> <span className="font-medium">{partialDialog.activity.name}</span></p>
                                    <p><span className="text-[#6B7280]">Remaining balance:</span> <span className="font-bold text-[#064E3B]">€{partialDialog.remaining.toFixed(2)}</span></p>
                                </div>
                                <div className="space-y-1">
                                    <label className="text-sm font-medium">Amount (€)</label>
                                    <Input
                                        type="number"
                                        min="0.01"
                                        step="0.01"
                                        max={partialDialog.remaining}
                                        value={partialAmountInput}
                                        onChange={(e) => setPartialAmountInput(e.target.value)}
                                        placeholder="e.g. 50.00"
                                        data-testid="partial-amount-input"
                                    />
                                </div>
                            </div>
                        )}
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setPartialDialog(null)}>Cancel</Button>
                            <Button className="bg-[#064E3B] hover:bg-[#047857]" onClick={submitPartial} data-testid="submit-partial-amount">
                                Continue
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>

                {/* Payment confirmation dialog */}
                <Dialog open={!!confirmDialog} onOpenChange={(o) => !o && setConfirmDialog(null)}>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle className="font-['Outfit']">Confirm payment</DialogTitle>
                            <DialogDescription>You&rsquo;ll be redirected to Stripe to complete this payment securely.</DialogDescription>
                        </DialogHeader>
                        {confirmDialog && (
                            <div className="space-y-3 text-sm">
                                <div className="bg-[#FAF9F6] rounded-lg p-4 space-y-1">
                                    <p><span className="text-[#6B7280]">Activity:</span> <span className="font-medium">{confirmDialog.activity.name}</span></p>
                                    <p><span className="text-[#6B7280]">{confirmDialog.students.length === 1 ? 'Student' : `Students (${confirmDialog.students.length})`}:</span></p>
                                    <ul className="list-disc list-inside pl-2 text-[#1F2937]">
                                        {confirmDialog.students.map(s => <li key={s.student_id}>{s.student_name}</li>)}
                                    </ul>
                                    <div className="flex items-center gap-2 pt-1"><span className="text-[#6B7280]">Phase:</span> <Badge variant="outline" className="text-xs capitalize">{confirmDialog.phase}</Badge></div>
                                    <p className="pt-2 mt-2 border-t border-[#E5E7EB] text-base">
                                        <span className="text-[#6B7280]">Total amount:</span>{' '}
                                        <span className="font-bold text-[#064E3B] text-lg">€{Number(confirmDialog.amount).toFixed(2)}</span>
                                    </p>
                                </div>
                            </div>
                        )}
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setConfirmDialog(null)}>Cancel</Button>
                            <Button
                                className="bg-[#064E3B] hover:bg-[#047857]"
                                onClick={() => {
                                    if (confirmDialog) {
                                        const { activity, students, phase, customAmount } = confirmDialog;
                                        setConfirmDialog(null);
                                        const ids = students.map(s => s.student_id);
                                        startCheckout({
                                            activityId: activity.id,
                                            studentIds: ids.length > 1 ? ids : ids[0],
                                            phase,
                                            customAmount,
                                        });
                                    }
                                }}
                                data-testid="proceed-to-stripe"
                            >
                                Proceed to Stripe
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>
        </DashboardLayout>
    );
};

// ---- New activity row (open for participation) ----
const ActivityRow = ({ activity, onPaySingle, onPayBatch, selected, onToggle, onClearSelection }) => {
    const today = new Date().toISOString().slice(0, 10);
    const closing = activity.closing_date || '';
    const daysToClose = useMemo(() => {
        if (!closing) return null;
        return Math.ceil((new Date(closing) - new Date(today)) / (1000 * 60 * 60 * 24));
    }, [closing, today]);
    const isClosed = !!activity.is_closed_for_participation;

    const cost = Number(activity.cost || 0);
    const deposit = Number(activity.deposit || 0);
    const hasDeposit = deposit > 0 && deposit < cost;

    const selectableStudents = (activity.students || []).filter(s => s.eligible && !s.registered);
    const selectedList = selectableStudents.filter(s => selected.has(s.student_id));
    const allSelected = selectableStudents.length > 0 && selectedList.length === selectableStudents.length;

    const selectAll = () => {
        selectableStudents.forEach(s => { if (!selected.has(s.student_id)) onToggle(activity.id, s.student_id); });
    };

    let banner = null;
    if (isClosed) banner = <div className="bg-[#F3F4F6] border-l-4 border-[#6B7280] text-[#374151] px-3 py-2 rounded text-xs flex items-center gap-2"><Lock className="h-3.5 w-3.5" />Registration is closed — kept here for records until reopened</div>;
    else if (daysToClose !== null && daysToClose <= 3) banner = <div className="bg-[#FEF2F2] border-l-4 border-[#EF4444] text-[#991B1B] px-3 py-2 rounded text-xs flex items-center gap-2 font-medium"><AlertTriangle className="h-3.5 w-3.5" />Closes in {daysToClose} day{daysToClose === 1 ? '' : 's'} ({closing})</div>;
    else if (daysToClose !== null && daysToClose <= 7) banner = <div className="bg-[#FFFBEB] border-l-4 border-[#F59E0B] text-[#92400E] px-3 py-2 rounded text-xs flex items-center gap-2"><AlertTriangle className="h-3.5 w-3.5" />Closes in {daysToClose} days ({closing})</div>;

    const handleBatch = (phase) => {
        if (selectedList.length === 0) { toast.error('Select at least one child to pay for'); return; }
        onPayBatch(activity, selectedList, phase);
    };

    return (
        <Card className={`card-surface ${isClosed ? 'opacity-70' : ''}`} data-testid={`activity-row-${activity.id}`}>
            <CardContent className="p-5 space-y-3">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                    <div>
                        <h3 className="text-lg font-semibold text-[#1F2937] font-['Outfit']">{activity.name}</h3>
                        {activity.description && <p className="text-sm text-[#4B5563] mt-1">{activity.description}</p>}
                        <div className="flex flex-wrap items-center gap-3 text-xs text-[#6B7280] mt-2">
                            <span className="flex items-center gap-1"><Calendar className="h-3 w-3" />{activity.start_date} → {activity.end_date}</span>
                            {activity.location && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{activity.location}</span>}
                            <span className="flex items-center gap-1"><Clock className="h-3 w-3" />Closes {closing || '—'}</span>
                        </div>
                    </div>
                    <div className="text-right">
                        <p className="text-2xl font-bold text-[#064E3B] tabular-nums">€{cost.toFixed(2)}</p>
                        <p className="text-xs text-[#6B7280]">per child{hasDeposit ? ` · deposit €${deposit.toFixed(2)}` : ''}</p>
                    </div>
                </div>

                {banner}

                {!isClosed && selectableStudents.length >= 2 && (
                    <div className="flex flex-wrap items-center justify-between gap-2 bg-[#FFFBEB] border border-[#FED7AA] rounded-lg px-3 py-2" data-testid={`batch-toolbar-${activity.id}`}>
                        <div className="flex items-center gap-2 text-xs">
                            <button
                                type="button"
                                className="text-[#9A3412] underline"
                                onClick={() => allSelected ? onClearSelection(activity.id) : selectAll()}
                                data-testid={`select-all-${activity.id}`}
                            >
                                {allSelected ? 'Clear selection' : `Select all (${selectableStudents.length})`}
                            </button>
                            <span className="text-[#9A3412]/80">·</span>
                            <span className="text-[#9A3412] font-medium">{selectedList.length} selected</span>
                        </div>
                        {selectedList.length > 0 && (
                            <div className="flex gap-2">
                                {hasDeposit && (
                                    <Button size="sm" variant="outline" className="border-[#F59E0B] text-[#9A3412]"
                                        onClick={() => handleBatch('deposit')}
                                        data-testid={`batch-pay-deposit-${activity.id}`}>
                                        Pay deposit × {selectedList.length} (€{(deposit * selectedList.length).toFixed(2)})
                                    </Button>
                                )}
                                <Button size="sm" className="bg-[#064E3B] hover:bg-[#047857] text-white"
                                    onClick={() => handleBatch('full')}
                                    data-testid={`batch-pay-full-${activity.id}`}>
                                    Pay full × {selectedList.length} (€{(cost * selectedList.length).toFixed(2)})
                                </Button>
                            </div>
                        )}
                    </div>
                )}

                <div className="space-y-2 pt-2 border-t border-[#F3F4F6]">
                    {activity.students.map(s => {
                        const canBatchSelect = !isClosed && s.eligible && !s.registered && selectableStudents.length >= 2;
                        const isChecked = selected.has(s.student_id);
                        return (
                            <div key={s.student_id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 py-2" data-testid={`student-row-${s.student_id}`}>
                                <div className="flex items-center gap-3 min-w-0">
                                    {canBatchSelect ? (
                                        <input
                                            type="checkbox"
                                            checked={isChecked}
                                            onChange={() => onToggle(activity.id, s.student_id)}
                                            className="h-4 w-4 rounded border-[#D1D5DB] cursor-pointer shrink-0"
                                            data-testid={`select-student-${activity.id}-${s.student_id}`}
                                        />
                                    ) : null}
                                    <div className="min-w-0">
                                        <p className={`font-medium truncate ${!s.eligible ? 'text-[#9CA3AF]' : 'text-[#1F2937]'}`}>{s.student_name}</p>
                                        {!s.eligible && <p className="text-xs text-[#9CA3AF] italic">Not eligible</p>}
                                    </div>
                                </div>
                                <div className="flex gap-2 shrink-0">
                                    {isClosed ? (
                                        <span className="text-xs text-[#6B7280] italic">Registration closed</span>
                                    ) : !s.eligible ? null : (
                                        <>
                                            {hasDeposit && (
                                                <Button size="sm" variant="outline" onClick={() => onPaySingle(activity, s, 'deposit')} data-testid={`pay-deposit-${activity.id}-${s.student_id}`}>
                                                    Pay deposit €{deposit.toFixed(2)}
                                                </Button>
                                            )}
                                            <Button size="sm" className="bg-[#064E3B] hover:bg-[#047857]" onClick={() => onPaySingle(activity, s, 'full')} data-testid={`pay-full-${activity.id}-${s.student_id}`}>
                                                Pay full €{cost.toFixed(2)}
                                            </Button>
                                        </>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </CardContent>
        </Card>
    );
};

// ---- Participated activity card (click to expand + payment history) ----
const ParticipatedActivityCard = ({ activity, expanded, onToggle, onPaySingle, onPayPartial }) => {
    const cost = Number(activity.cost || 0);
    const registeredStudents = (activity.students || []).filter(s => s.registered);
    const totalPaid = registeredStudents.reduce((sum, s) => sum + Number(s.amount_paid || 0), 0);
    const totalDue = registeredStudents.reduce((sum, s) => sum + Number(s.amount_due || 0), 0);
    const isFinished = !!activity.is_finished;

    return (
        <Card className="card-surface" data-testid={`participated-activity-${activity.id}`}>
            <CardContent className="p-0">
                <button
                    type="button"
                    onClick={onToggle}
                    className="w-full p-5 flex items-start justify-between gap-3 hover:bg-[#FAF9F6] transition-colors text-left"
                    data-testid={`participated-toggle-${activity.id}`}
                >
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="text-lg font-semibold text-[#1F2937] font-['Outfit']">{activity.name}</h3>
                            {isFinished && <Badge className="bg-[#6B7280] text-white text-xs">Finished</Badge>}
                            {!isFinished && activity.is_closed_for_participation && <Badge variant="outline" className="text-xs"><Lock className="h-3 w-3 mr-1" />Closed</Badge>}
                            <Badge variant="secondary" className="text-xs">{registeredStudents.length} {registeredStudents.length === 1 ? 'child' : 'children'}</Badge>
                        </div>
                        <div className="flex flex-wrap items-center gap-3 text-xs text-[#6B7280] mt-1">
                            <span className="flex items-center gap-1"><Calendar className="h-3 w-3" />{activity.start_date} → {activity.end_date}</span>
                            {activity.location && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{activity.location}</span>}
                        </div>
                        <div className="flex flex-wrap items-center gap-3 mt-2 text-sm">
                            <span className="text-[#10B981]"><span className="text-[#6B7280]">Paid:</span> <span className="font-semibold tabular-nums">€{totalPaid.toFixed(2)}</span></span>
                            {totalDue > 0 && <span className="text-[#F59E0B]"><span className="text-[#6B7280]">Remaining:</span> <span className="font-semibold tabular-nums">€{totalDue.toFixed(2)}</span></span>}
                            {totalDue <= 0 && <Badge className="bg-[#10B981] text-white text-xs">Fully paid</Badge>}
                        </div>
                    </div>
                    {expanded ? <ChevronUp className="h-5 w-5 text-[#6B7280] shrink-0 mt-1" /> : <ChevronDown className="h-5 w-5 text-[#6B7280] shrink-0 mt-1" />}
                </button>

                {expanded && (
                    <div className="border-t border-[#F3F4F6] p-5 space-y-3 bg-[#FDFCFA]" data-testid={`participated-detail-${activity.id}`}>
                        {activity.description && <p className="text-sm text-[#4B5563]">{activity.description}</p>}
                        <div className="space-y-2">
                            {registeredStudents.map(s => {
                                const paid = Number(s.amount_paid || 0);
                                const due = Number(s.amount_due || 0);
                                const discount = Number(s.discount_applied || 0);
                                const isPaid = due <= 0;
                                return (
                                    <div key={s.student_id} className="rounded-lg border border-[#E5E7EB] bg-white p-3" data-testid={`participated-student-${s.student_id}`}>
                                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                                            <div>
                                                <p className="font-medium text-[#1F2937] flex items-center gap-2">
                                                    {isPaid && <CheckCircle2 className="h-4 w-4 text-[#10B981]" />}
                                                    {s.student_name}
                                                </p>
                                                <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-xs mt-1">
                                                    <span className="text-[#6B7280]">Fee: <span className="text-[#1F2937] tabular-nums">€{cost.toFixed(2)}</span></span>
                                                    {discount > 0 && <span className="text-[#6B7280]">Discount: <span className="text-[#10B981] tabular-nums">−€{discount.toFixed(2)}</span></span>}
                                                    <span className="text-[#6B7280]">Paid: <span className="text-[#10B981] tabular-nums">€{paid.toFixed(2)}</span></span>
                                                    <span className="text-[#6B7280]">Remaining: <span className={`tabular-nums ${due > 0 ? 'text-[#F59E0B]' : 'text-[#10B981]'}`}>€{due.toFixed(2)}</span></span>
                                                </div>
                                                {(s.group || s.room) && (
                                                    <div className="flex gap-2 mt-1 text-xs text-[#6B7280]">
                                                        {s.group && <span>Group: <span className="text-[#1F2937]">{s.group}</span></span>}
                                                        {s.room && <span>Room: <span className="text-[#1F2937]">{s.room}</span></span>}
                                                    </div>
                                                )}
                                            </div>
                                            {!isPaid && (
                                                <div className="flex gap-2 flex-wrap">
                                                    <Button size="sm" variant="outline" onClick={() => onPayPartial(activity, s)} data-testid={`pay-partial-${s.student_id}`}>
                                                        <Wallet className="h-4 w-4 mr-1" />Pay partial
                                                    </Button>
                                                    <Button size="sm" className="bg-[#064E3B] hover:bg-[#047857] text-white" onClick={() => onPaySingle(activity, s, 'balance')} data-testid={`pay-remaining-${s.student_id}`}>
                                                        Pay remaining €{due.toFixed(2)}
                                                    </Button>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        <p className="text-xs text-[#9CA3AF] italic">Activity receipts are shown here and not mixed with School Fees receipts.</p>
                    </div>
                )}
            </CardContent>
        </Card>
    );
};

export default Activities;
